import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/Page";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";

export default function LegalRegister() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const { data: rows } = useQuery({ queryKey: ["legal-register"], queryFn: async () => (await api.get("/legal/register")).data });

  const statuses = useMemo(() => Array.from(new Set((rows || []).map((r) => r.status).filter(Boolean))), [rows]);
  const filtered = (rows || []).filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [r.contract_ref, r.customer, r.product, r.document, r.owner].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div data-testid="legal-register-page">
      <PageHeader title="Legal Register" subtitle="Searchable register of all contracts and agreements." />
      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref, customer, product, owner…" className="border-[#2D3F62] bg-[#0B1120] pl-9 text-slate-100" data-testid="register-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-52 border-[#2D3F62] bg-[#0B1120] text-slate-100" data-testid="register-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#1E2D4A]">
        <table className="w-full text-sm">
          <thead className="bg-[#0E1729] text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              {["Ref", "Customer", "Product", "Document", "Status", "Version", "Effective", "Expiry", "Notice Deadline", "Value", "Owner"].map((h) => <th key={h} className="whitespace-nowrap px-3 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1E2D4A]">
            {filtered.length === 0 && <tr><td colSpan={11} className="px-4 py-10 text-center text-sm text-slate-500" data-testid="register-empty">No contracts recorded yet.</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} className="cursor-pointer bg-[#111A2E]/40 hover:bg-[#17233B]/60" onClick={() => navigate(`/legal/contracts/${r.id}`)} data-testid={`register-row-${r.id}`}>
                <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-cyan-300">{r.contract_ref}</td>
                <td className="px-3 py-2.5 text-slate-100">{r.customer}</td>
                <td className="px-3 py-2.5 text-slate-400">{r.product || "—"}</td>
                <td className="px-3 py-2.5 text-slate-400">{r.document}</td>
                <td className="px-3 py-2.5 text-slate-300">{r.status}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-400">{r.version || "—"}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-slate-400">{r.effective_date || "—"}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-slate-400">{r.expiry_date || "—"}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-amber-300">{r.notice_deadline || "—"}</td>
                <td className="px-3 py-2.5 text-slate-300">{r.contract_value ?? "—"}</td>
                <td className="px-3 py-2.5 text-slate-400">{r.owner || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
